
import { useState } from 'react';

interface FaqAccordionItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  categoryLabel: string;
  categoryIcon: string;
}

function FaqAccordionItem({ question, answer, isOpen, onToggle, categoryLabel, categoryIcon }: FaqAccordionItemProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 text-left flex items-start justify-between gap-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${categoryIcon} text-emerald-600 text-xs`}></i>
            </div>
            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
              {categoryLabel}
            </span>
          </div>
          <span className="font-bold text-gray-900 leading-snug block">
            {question}
          </span>
        </div>
        <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-1 bg-emerald-50 rounded-full">
          <i
            className={`ri-arrow-down-s-line text-xl text-emerald-700 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[500px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-4">
          {answer}
        </div>
      </div>
    </div>
  );
}

interface FaqItem {
  id: number;
  category: string;
  question: string;
  answer: string;
}

interface FaqListProps {
  items: FaqItem[];
  categoryMap: Record<string, { label: string; icon: string }>;
}

export default function FaqList({ items, categoryMap }: FaqListProps) {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleItem = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
          <i className="ri-search-line text-3xl text-gray-400"></i>
        </div>
        <p className="text-gray-500 text-lg">No questions match your search.</p>
        <p className="text-gray-400 text-sm mt-1">Try a different keyword or browse by category.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const cat = categoryMap[item.category] || { label: item.category, icon: 'ri-question-line' };
        return (
          <FaqAccordionItem
            key={item.id}
            question={item.question}
            answer={item.answer}
            isOpen={openId === item.id}
            onToggle={() => toggleItem(item.id)}
            categoryLabel={cat.label}
            categoryIcon={cat.icon}
          />
        );
      })}
    </div>
  );
}
